/**
 * Id-keyed registry of persisted search-param declarations.
 * Same id replaces (Vite HMR re-evaluates the module in place). A different
 * id claiming a taken param name is a conflict — two owners would silently
 * copy each other's values into storage.
 */

import type { PersistedSearchParamsDeclaration } from "./createPersistedSearchParams.types";

const byId = new Map<string, PersistedSearchParamsDeclaration>();
const listeners = new Set<() => void>();
const resetListeners = new Set<() => void>();

/**
 * Whether to throw on two features claiming the same URL param.
 *
 * Why: that throw is a "you wrote it wrong" alarm for developers. In a
 * production build we skip it so a shipped mistake does not white-screen
 * the app. Vite's `import.meta.env.PROD` is the real production flag; unit
 * tests run under tsx where that may not exist, so we catch and fall back
 * to NODE_ENV — otherwise the uniqueness tests would never fire.
 */
const isDevUniquenessCheck = (): boolean => {
  try {
    if (import.meta.env.PROD) return false;
  } catch {
    // import.meta.env may be missing under tsx
  }
  return process.env.NODE_ENV !== "production";
};

const notify = (): void => {
  for (const listener of listeners) listener();
};

const otherOwnerOfParam = (param: string, selfId: string): string | undefined => {
  for (const [id, declaration] of byId) {
    if (id === selfId) continue;
    if (declaration.params.some((entry) => entry.param === param)) return id;
  }
  return undefined;
};

/**
 * Features call this once when their prefs module loads.
 *
 * Why: Sync lives at the app root and must not import every page. Pages
 * announce themselves here instead. Same id overwrites (Vite hot-reload
 * re-runs the module). A *different* id grabbing the same URL param throws
 * in development — two owners would copy each other's saved values. Then
 * we ping subscribers so a chunk that loaded late still gets its URL filled.
 */
export const registerPersistedSearchParams = (
  declaration: PersistedSearchParamsDeclaration
): void => {
  if (isDevUniquenessCheck()) {
    for (const { param } of declaration.params) {
      const otherId = otherOwnerOfParam(param, declaration.id);
      if (otherId !== undefined) {
        throw new Error(
          `Persisted search param "${param}" is already claimed by declaration "${otherId}"; cannot register "${declaration.id}"`
        );
      }
    }
  }

  byId.set(declaration.id, declaration);
  notify();
};

/** Copy of what's registered right now. Sync reads this each pass so it never walks a live Map that HMR or a late import could mutate mid-reconcile. */
export const getPersistedSearchParamsSnapshot =
  (): PersistedSearchParamsDeclaration[] => [...byId.values()];

/**
 * Fires when a feature registers (or re-registers on HMR).
 *
 * Why: the app-root Sync mounts before lazy pages. Task Performance / group-by
 * prefs only register when that chunk loads. Without this, those params would
 * stay URL-only until the next navigation. Sync bumps an epoch so it can fill
 * the URL as soon as the declaration appears.
 */
export const subscribePersistedSearchParams = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Fires after "reset all preferences" has already wiped localStorage.
 *
 * Why a separate channel: the reset helper cannot touch the URL (no router
 * there). Sync listens here, then strips the current screen's bound params so
 * the just-cleared values are not immediately copied back from the address bar.
 */
export const subscribePersistedSearchParamsReset = (
  listener: () => void
): (() => void) => {
  resetListeners.add(listener);
  return () => {
    resetListeners.delete(listener);
  };
};

/** Raised by `resetAllLocalPrefs` after slices are cleared. URL removal happens in PersistedSearchParamsSync. */
export const raisePersistedSearchParamsReset = (): void => {
  for (const listener of resetListeners) listener();
};

/** Test-only: empty the registry between cases. */
export const resetPersistedSearchParamsRegistry = (): void => {
  byId.clear();
  listeners.clear();
  resetListeners.clear();
};
